import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { api } from '../lib/gapApi';

export const AnalysisProgress = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('QUEUED');
  const [error, setError] = useState(null);

  useEffect(() => {
    let interval;

    const fetchStatus = async () => {
      try {
        const { data } = await api.get(`/analysis/${id}/status`);
        setStatus(data.status);
        if (data.error_message) {
          setError(data.error_message);
        }

        if (data.status === 'COMPLETED' || data.status === 'FAILED') {
          clearInterval(interval);
        }
      } catch (err) {
        console.error('Failed to fetch status', err);
      }
    };

    fetchStatus();
    interval = setInterval(fetchStatus, 3000);

    return () => clearInterval(interval);
  }, [id]);

  const renderIcon = () => {
    if (status === 'FAILED') return <XCircle className="text-red-500 w-16 h-16" />;
    if (status === 'COMPLETED') return <CheckCircle className="text-emerald-500 w-16 h-16" />;
    if (status === 'QUEUED') return <Clock className="text-slate-400 w-16 h-16" />;
    return <Loader2 className="animate-spin text-blue-500 w-16 h-16" />;
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'QUEUED': return 'Analysis Queued...';
      case 'DISCOVERING': return 'Discovering Company Profile...';
      case 'COLLECTING': return 'Collecting Public Data... (Phase 3)';
      case 'ANALYZING': return 'AI Reasoner Analyzing Evidence... (Phase 3)';
      case 'COMPLETED': return 'Analysis Completed!';
      case 'FAILED': return 'Analysis Failed.';
      default: return 'Processing...';
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto min-h-[80vh] flex flex-col items-center justify-center">
      <div className="bg-slate-800 p-12 rounded-3xl border border-slate-700 shadow-2xl flex flex-col items-center w-full max-w-lg text-center">
        <div className="mb-6 bg-slate-900 p-6 rounded-full shadow-inner">
          {renderIcon()}
        </div>

        <h2 className="text-2xl font-bold text-white mb-2">{getStatusMessage()}</h2>

        {status === 'FAILED' && error && (
          <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="mt-8 space-y-4 w-full">
          <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ease-in-out ${
                status === 'FAILED' ? 'bg-red-500' :
                status === 'COMPLETED' ? 'bg-emerald-500' : 'bg-blue-500 relative overflow-hidden'
              }`}
              style={{
                width: status === 'QUEUED' ? '10%' :
                       status === 'DISCOVERING' ? '30%' :
                       status === 'COLLECTING' ? '60%' :
                       status === 'ANALYZING' ? '85%' :
                       status === 'COMPLETED' ? '100%' : '100%'
              }}
            >
              {status !== 'COMPLETED' && status !== 'FAILED' && (
                <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
              )}
            </div>
          </div>

          <div className="flex justify-between text-xs text-slate-500 px-1 font-medium">
            <span className={status === 'QUEUED' ? 'text-blue-400' : 'text-slate-300'}>Queued</span>
            <span className={status === 'DISCOVERING' ? 'text-blue-400' : status === 'QUEUED' ? 'text-slate-500' : 'text-slate-300'}>Discovering</span>
            <span className={status === 'COLLECTING' ? 'text-blue-400' : (status === 'QUEUED' || status === 'DISCOVERING') ? 'text-slate-500' : 'text-slate-300'}>Collecting</span>
            <span className={status === 'COMPLETED' ? 'text-emerald-400' : status === 'FAILED' ? 'text-red-400' : 'text-slate-500'}>Done</span>
          </div>
        </div>

        {status === 'COMPLETED' && (
          <button
            onClick={() => navigate(`/analyzer/analysis/${id}/consultant`)}
            className="mt-8 bg-blue-600 hover:bg-blue-500 text-white font-medium py-2 px-6 rounded-lg transition-colors"
          >
            View Multi-Agent Results
          </button>
        )}
      </div>
    </div>
  );
};

export default AnalysisProgress;
